import React, { useEffect } from 'react'
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { springVariants, leftVariants, springViewport } from '../utils/framerVariants'

const StickyTextAnimation = ({ data }) => {

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const { Headline, Text, Animations, TextFold } = data;
  const hasMultipleAnimations = Animations && Animations.length > 1;

  const isGerman = typeof window !== 'undefined' && window.location.pathname.includes('/de/');

  const animatedDecimal = (initialValue, decimalPlaces) => {
    const count = useMotionValue(0 || 0); // Set initial value to 0 if empty or null

    useEffect(() => {
      if (inView) {
        const animation = animate(count, initialValue, {
          duration: 1,
          delay: 0.3,
        });
        return animation.stop;
      }
    }, [inView]);

    const formattedDecimal = useTransform(count, (value) => {
      const decimalDivider = isGerman ? ',' : '.';
      const formattedValue = value.toLocaleString(decimalPlaces).replace(',', decimalDivider);
      return formattedValue;
    });

    return formattedDecimal;
  };

  return (
    <article className="container mx-auto max-w-6xl lg:max-w-7xl 2xl:max-w-8xl pb-16 md:pb-24 lg:pb-32 xl:pb-48 mb-16 md:mb-24 lg:mb-32 xl:mb-48 px-4 md:px-24 lg:px-24 xl:px-24">
      <div className="flex sm:flex-row flex-col">
        <div className="sm:basis-5/12 basis-full pr-0 lg:pr-12">
          <div className="sm:sticky sm:top-48 mb-8 md:mb-0">
            <motion.div
              variants={leftVariants}
              initial="invisible"
              whileInView="visible"
              viewport={springViewport}
              transition={{ delay: 0.2 }}
              className="absolute scharnier-form hidden xl:block 6xl:hidden 2xl-ml-[10%] 4xl:-ml-[20%]"
            >
              <div className="after:bg-black before:bg-black w-[1100px] h-[4px] after:translate-x-[3px] after:translate-y-[69px] after:w-[1100px] after:h-[4px] after:absolute after:content-[''] after:rotate-[-59deg] after:left-[-885px] after:top-[418px] before:translate-x-[-5px] before:translate-y-[-21px] before:w-[1100px] before:h-[4px] before:absolute before:content-[''] before:rotate-[25deg] before:left-[-1090px] before:top-[-196px]"
              ></div>
            </motion.div>
            {Headline && (
              <motion.h2
                variants={springVariants}
                initial="invisible"
                whileInView="visible"
                viewport={springViewport}
                className="text-headline-md tracking-headline-md leading-headline-md font-bold"
              >
                {Headline}
              </motion.h2>
            )}
          </div>
        </div>
        <div className="richtext sm:basis-7/12 basis-full pl-0 sm:pl-12 md:pl-16 lg:pl-32 -mb-0 md:-mb-24 lg:-mb-32 xl:-mb-48">
          {Text && (
            <motion.div
              variants={springVariants}
              initial="invisible"
              whileInView="visible"
              viewport={springViewport}
              dangerouslySetInnerHTML={{ __html: Text }}
            />
          )}
          {Animations && (
            <motion.div
              variants={springVariants}
              initial="invisible"
              whileInView="visible"
              viewport={springViewport}
              className="flex flex-col items-center justify-center mt-16 lg:mt-24 mb-16 lg:mb-24 text-center"
              ref={ref}
            >
              {Animations.map((animation, index) => {
                const formattedNumber = animatedDecimal(animation.DecimalNumber, animation.DecimalPlaces);

                return (
                  <React.Fragment key={index}>
                    <div className="number">
                      <h2>
                        <motion.span>{formattedNumber}</motion.span>
                        {animation.NumberUnit}
                      </h2>
                    </div>
                    <div className="text mt-4 px-16 md:px-16 lg:px-24 xl:px-32">
                      <p className="mb-0 text-text-sm tracking-text-sm leading-text-sm">
                        {animation.NumberDescription}
                      </p>
                    </div>
                    {hasMultipleAnimations &&
                      index !== Animations.length - 1 && (
                        <div className="line w-[4px] h-32 bg-black mt-8 mb-7" />
                      )}
                  </React.Fragment>
                );
              })}
            </motion.div>
          )}
          {TextFold && (
            <motion.div
              variants={springVariants}
              initial="invisible"
              whileInView="visible"
              viewport={springViewport}
              dangerouslySetInnerHTML={{ __html: TextFold }}
            />
          )}
        </div>
      </div>
    </article>
  );
}

export default StickyTextAnimation;
